import { KeyboardEvent, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useUpdateWorkflowState } from '@/service-library/hooks/workflow-states'
import { WorkflowState } from '@/types/workflows'
import { FormTextField, PixyDocsForm } from '@/components/forms'

type FormValues = {
  name: string
}

type WorkflowStateNameFieldProps = {
  /** Put on the input, so a `SettingsRow` label can point at it. */
  id: string
  workflowState: WorkflowState
  updateWorkflowState: ReturnType<
    typeof useUpdateWorkflowState
  >['updateWorkflowState']
}

/**
 * The workflow state's name, edited in place. A name that isn't empty saves
 * on Enter or when the field loses focus. Escape puts back the saved name.
 */
export default function WorkflowStateNameField({
  id,
  workflowState,
  updateWorkflowState,
}: WorkflowStateNameFieldProps) {
  const methods = useForm<FormValues>({
    defaultValues: { name: workflowState.name },
    mode: 'onChange',
  })

  const { formState, handleSubmit, reset } = methods
  const nameError = formState.errors.name

  // Follow the saved name, including the rollback when a save fails.
  useEffect(() => {
    reset({ name: workflowState.name })
  }, [workflowState.name, reset])

  function onSubmit({ name }: FormValues) {
    const trimmedName = name.trim()
    if (trimmedName === workflowState.name) return
    updateWorkflowState({ ...workflowState, name: trimmedName })
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key !== 'Escape') return
    reset({ name: workflowState.name })
    ;(e.target as HTMLElement).blur()
  }

  return (
    <PixyDocsForm
      methods={methods}
      onSubmit={onSubmit}
      id="workflow-state-name-form"
    >
      <FormTextField
        id={id}
        name="name"
        variant="outlined"
        size="small"
        required
        requiredErrorText="Name cannot be empty."
        error={!!nameError}
        helperText={nameError?.message}
        onBlur={() => handleSubmit(onSubmit)()}
        onKeyDown={handleKeyDown}
        sx={{ width: 280 }}
        useControllerProps={{
          rules: {
            validate: (value: string) =>
              value.trim().length > 0 || 'Name cannot be empty.',
          },
        }}
      />
    </PixyDocsForm>
  )
}
